/**
 * Validation utilities for input validation and sanitization.
 * Following security best practices to prevent injection attacks.
 */

import type { Coordinates } from '../types';

/**
 * Minimum travel time in minutes.
 */
export const MIN_TRAVEL_TIME = 1;

/**
 * Maximum travel time in minutes.
 */
export const MAX_TRAVEL_TIME = 120;

/**
 * Validates latitude value.
 * @param lat - Latitude to validate
 * @returns true if valid, false otherwise
 */
export function validateLatitude(lat: unknown): boolean {
  return (
    typeof lat === 'number' &&
    !isNaN(lat) &&
    isFinite(lat) &&
    lat >= -90 &&
    lat <= 90
  );
}

/**
 * Validates longitude value.
 * @param lng - Longitude to validate
 * @returns true if valid, false otherwise
 */
export function validateLongitude(lng: unknown): boolean {
  return (
    typeof lng === 'number' &&
    !isNaN(lng) &&
    isFinite(lng) &&
    lng >= -180 &&
    lng <= 180
  );
}

/**
 * Validates coordinates object.
 * @param coords - Coordinates object to validate
 * @returns true if valid, false otherwise
 */
export function validateCoordinates(coords: unknown): coords is Coordinates {
  if (typeof coords !== 'object' || coords === null) {
    return false;
  }

  const c = coords as Coordinates;
  return validateLatitude(c.lat) && validateLongitude(c.lng);
}

/**
 * Validates travel time value.
 * @param time - Time in minutes to validate
 * @returns true if valid, false otherwise
 */
export function validateTravelTime(time: unknown): boolean {
  return (
    typeof time === 'number' &&
    !isNaN(time) &&
    isFinite(time) &&
    time >= MIN_TRAVEL_TIME &&
    time <= MAX_TRAVEL_TIME &&
    Number.isInteger(time)
  );
}

/**
 * Sanitizes coordinate values by clamping to valid ranges.
 * @param lat - Latitude to sanitize
 * @param lng - Longitude to sanitize
 * @returns Sanitized coordinates object
 */
export function sanitizeCoordinates(
  lat: number,
  lng: number
): Coordinates {
  const sanitizedLat = Math.max(-90, Math.min(90, lat));
  const sanitizedLng = Math.max(-180, Math.min(180, lng));
  return { lat: sanitizedLat, lng: sanitizedLng };
}

/**
 * Sanitizes travel time by clamping to valid range.
 * @param time - Time in minutes to sanitize
 * @returns Sanitized time value
 */
export function sanitizeTravelTime(time: number): number {
  return Math.max(
    MIN_TRAVEL_TIME,
    Math.min(MAX_TRAVEL_TIME, Math.round(time))
  );
}

/**
 * Validates and sanitizes a numeric input from user.
 * Prevents NaN and Infinity values.
 * @param value - Value to validate
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns Validated number or null if invalid
 */
export function validateNumericInput(
  value: unknown,
  min: number,
  max: number
): number | null {
  if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
    return null;
  }
  if (value < min || value > max) {
    return null;
  }
  return value;
}

/**
 * Creates an error message for invalid coordinates.
 * @returns Error message string
 */
export function getInvalidCoordinatesError(): string {
  return 'Invalid coordinates. Latitude must be between -90 and 90, longitude must be between -180 and 180.';
}

/**
 * Creates an error message for invalid travel time.
 * @returns Error message string
 */
export function getInvalidTravelTimeError(): string {
  return `Invalid travel time. Must be between ${MIN_TRAVEL_TIME} and ${MAX_TRAVEL_TIME} minutes.`;
}

